import { Section } from '~/components/common/section';
import {
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
} from 'recharts';
import { FC, useEffect, useState } from 'react';
import { Translate } from '~/i18n';
import useSalesStatistics from '~/apis/dashboard-statistics/useSalesStatistics';
import { DateTime } from 'luxon';
import { SubmitLoader } from '~/components/common/loaders/submit';
import DateRangePicker from '~/components/common/date-range-picker';
import EmptyState from './empty-states';

const CustomizedAxisTick: FC<any> = (props: any) => {
   const { x, y, payload } = props;

   return (
      <g transform={`translate(${x},${y})`}>
         <text x={0} y={0} dy={0} fontSize={14} textAnchor="end" fill="#B5B5C3">
            {payload.value}
         </text>
      </g>
   );
};

export const SalesChart = () => {
   //default start = 7 days ago end today
   const [dateRange, setRange] = useState([
      DateTime.now().minus({ days: 7 }).toFormat('yyyy-MM-dd'),
      DateTime.now().toFormat('yyyy-MM-dd'),
   ]);
   const [startDate, endDate] = dateRange;
   const { data: statisticsNumbers, isLoading } = useSalesStatistics({
      startDate,
      endDate,
   });
   const [chartData, setChartData] = useState([]);

   useEffect(() => {
      if (statisticsNumbers?.data?.length) {
         setChartData(statisticsNumbers?.data.reverse());
      }
   }, [statisticsNumbers]);

   return (
      <Section className=" py-8">
         <div className="px-5 mb-6 flex justify-between">
            <div>
               <span className="block mb-1 text-lg font-semibold">
                  <Translate id="dashboard:sales over months" />
               </span>
               <span className="block text-xs font-medium text-grey-300">
                  {statisticsNumbers?.data?.length}
                  {'  '}
                  <Translate id="dashboard:orders" />
               </span>
            </div>
            <div>
               <DateRangePicker onSubmitDates={range => setRange(range)} />
            </div>
         </div>
         <div className="w-full h-[250px] sm:h-[400px]">
            {isLoading ? (
               <div className="w-full h-full flex justify-center items-center">
                  <SubmitLoader />
               </div>
            ) : statisticsNumbers?.data?.length === 0 ? (
               <EmptyState />
            ) : (
               <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                     width={500}
                     height={400}
                     data={chartData.map((a: any) => ({
                        date: DateTime.fromISO(a.date).toMillis(),
                        count: a.count,
                     }))}
                     margin={{
                        top: 10,
                        right: 30,
                        left: 0,
                        bottom: 0,
                     }}>
                     <XAxis
                        dataKey="date"
                        // scale="time"
                        // type="number"
                        // domain={['auto', 'auto']}
                        stroke="#B5B5C3"
                        fontSize={14}
                        // tick={<CustomizedAxisTick />}
                        tickFormatter={name =>
                           DateTime.fromMillis(Number(name)).toFormat('yyyy-MM-dd')
                        }
                     />
                     <YAxis
                        dataKey="count"
                        type="number"
                        stroke="#B5B5C3"
                        fontSize={14}
                        tick={<CustomizedAxisTick />}
                     />
                     <CartesianGrid strokeDasharray="3 3" />
                     {statisticsNumbers?.data?.length > 0 && (
                        <Tooltip
                           labelFormatter={name => DateTime.fromMillis(name).toFormat('yyyy-MM-dd')}
                        />
                     )}
                     <Area
                        type="monotone"
                        dataKey="count"
                        strokeWidth={3}
                        stroke="#3699FF"
                        fill="#E1F0FF"
                     />
                  </AreaChart>
               </ResponsiveContainer>
            )}
         </div>
      </Section>
   );
};
